import { useEffect, useState } from "react";
import { fromApi, type Integration } from "../types/integration";

export type AdminIntegrationsStatus = "loading" | "ready" | "error";

export type UseAdminIntegrationsResult = {
  status: AdminIntegrationsStatus;
  integrations: ReadonlyArray<Integration>;
  error: string | null;
};

// Read-only admin view of a tenant's integrations (catalog + resolved
// per-tenant connection state). Powers the instance-detail Integrations panel;
// IP-353 removed the per-app pricing editor, so there is no longer a write path.
export const useAdminIntegrations = (
  tenantId: string | null,
): UseAdminIntegrationsResult => {
  const [status, setStatus] = useState<AdminIntegrationsStatus>("loading");
  const [integrations, setIntegrations] = useState<ReadonlyArray<Integration>>(
    [],
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (tenantId === null) {
      setIntegrations([]);
      setStatus("ready");
      setError(null);
      return;
    }
    let cancelled = false;
    setStatus("loading");
    setError(null);

    void fetch(`/api/admin/integrations/${encodeURIComponent(tenantId)}`, {
      credentials: "include",
    })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`admin integrations fetch failed (${res.status})`);
        }
        const raw = (await res.json()) as ReadonlyArray<
          Parameters<typeof fromApi>[0]
        >;
        if (cancelled) return;
        setIntegrations(raw.map(fromApi));
        setStatus("ready");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  return { status, integrations, error };
};
